'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type React from 'react';
import type { ReactNode } from 'react';
import { forwardRef, useId, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
export type CheckboxProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  hint?: ReactNode;
  id?: string;
  label?: ReactNode;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  required?: boolean;
  testID?: string;
  value?: string;
};
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((t0, ref) => {
  const $ = _c(35);
  const {
    checked,
    defaultChecked,
    disabled,
    hasError,
    hint,
    id: possibleId,
    label,
    name,
    onBlur,
    onChange,
    readOnly,
    required,
    testID,
    value
  } = t0;
  const inputRef = useRef(null);
  const internalId = useId();
  const id = possibleId || internalId;
  const t1 = "flex flex-col";
  const t2 = hint ? `${id}-hint` : undefined;
  const t3 = hasError ? "text-error" : "text-primary";
  let t4;
  if ($[0] !== t3) {
    t4 = cx("text-body-2 inline-flex items-center gap-3", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = hasError && "fp-checkbox-error";
  let t6;
  if ($[2] !== t5) {
    t6 = cx("fp-checkbox", "appearance-none grid place-content-center", "h-4 w-4 min-w-4 m-0 cursor-pointer", "outline-offset-2 transition-all duration-100 ease-in-out", "[&::before]:content-[\"\"] [&::before]:w-[0.55em] [&::before]:h-[0.55em]", "[&::before]:scale-0 [&::before]:transition-transform [&::before]:duration-100", "[&::before]:origin-bottom-left", "[&::before]:[clip-path:polygon(14%_44%,0_65%,50%_100%,100%_16%,80%_0%,43%_62%)]", "[&:checked::before]:scale-100", "disabled:cursor-not-allowed", t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = mergeRefs([inputRef, ref]);
  const t8 = disabled ? undefined : 0;
  let t9;
  if ($[4] !== checked || $[5] !== defaultChecked || $[6] !== disabled || $[7] !== id || $[8] !== name || $[9] !== onBlur || $[10] !== onChange || $[11] !== readOnly || $[12] !== required || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== value) {
    t9 = <input aria-checked={checked} aria-disabled={disabled} aria-required={required} checked={checked} className={t6} defaultChecked={defaultChecked} disabled={disabled} id={id} name={name} onBlur={onBlur} onChange={onChange} readOnly={readOnly} ref={t7} required={required} tabIndex={t8} type="checkbox" value={value} />;
    $[4] = checked;
    $[5] = defaultChecked;
    $[6] = disabled;
    $[7] = id;
    $[8] = name;
    $[9] = onBlur;
    $[10] = onChange;
    $[11] = readOnly;
    $[12] = required;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = value;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== label) {
    t10 = <span className="flex items-center">{label}</span>;
    $[18] = label;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== id || $[21] !== t10 || $[22] !== t2 || $[23] !== t4 || $[24] !== t9 || $[25] !== testID) {
    t11 = <label aria-describedby={t2} data-testid={testID} htmlFor={id} className={t4}>{t9}{t10}</label>;
    $[20] = id;
    $[21] = t10;
    $[22] = t2;
    $[23] = t4;
    $[24] = t9;
    $[25] = testID;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== hasError || $[28] !== hint || $[29] !== id || $[30] !== inputRef) {
    t12 = hint && <button type="button" id={`${id}-hint`} onClick={() => inputRef.current?.click()} className={cx("text-body-2 ml-[26px] mt-1 text-left cursor-pointer bg-transparent border-none p-0", hasError ? "text-error" : "text-tertiary")}>{hint}</button>;
    $[27] = hasError;
    $[28] = hint;
    $[29] = id;
    $[30] = inputRef;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <div className={t1}>{t11}{t12}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
});
export default Checkbox;